import {
  BodyMedium,
  BodySmall,
  DeprecatedCheckIcon,
  Pagination,
  SearchInput,
  Table,
  ToggleButton,
  Tooltip,
  theme,
  DeprecatedNoIcon,
} from '@arnold/common';
import { getEncodedCryptoAES, removeAccents, seqDateToString } from '@arnold/core';
import styled from '@emotion/styled/macro';
import { useEffect, useRef, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { URLs } from '../../lib/common';
import { ANONYMOUS_RESPONDENT_ID } from '../../lib/reportHelper';
import { AnswerType } from '../../lib/reports/question';
import { HighlightedAnswersIcon } from './HighlightedAnswerIcon';
import { useIsMobileView } from './ResponsiveSwitcher';
import { usePageParam, setScrollToParam, useScrollToRespondentTable } from '../../lib/reports/reportUtils';
import { usePushReportEvent } from '../ReportGTMContext';

interface IProps {
  data: IAlertBlock[];
  search: string;
  accessToken: string;
  anonymityEnabled: boolean;
  publicAccess: boolean;
  isHiring: boolean;
  reportId: string;
}

export interface IAnswer {
  text: string;
  type: AnswerType;
}

export interface IMessage {
  id: string;
  question: string;
  weight: number;
  answers: IAnswer[];
  date: Date;
  questionDefinitionIndex: string;
}

export interface IAlertBlock {
  respondentId: string;
  topicGroupId: string;
  surveyId: string;
  name: string;
  date: Date;
  team: string;
  position: string | null | undefined;
  isHired: boolean | null | undefined;
  isRejected: boolean | null | undefined;
  messages: IMessage[];
  seqDate?: string;
}

const LS_KEY_WITHOUT_ANSWER = 'hideRespondentsWithoutAnswer';
const LS_KEY_WITH_WARNING = 'hideRespondentsWithWarning';
const RESPONDENTS_ON_PAGE = 10;

const StyledSearchInput = styled(SearchInput)`
  width: 270px;
  @media (max-width: ${theme.breakpoint.small}) {
    width: 100%;
  }
`;

type TableProps = {
  containsAnonymity: boolean;
};
const StyledTable = styled(Table)`
  tr:first-child {
    background-color: ${theme.colors.backgroundBasic.default};
  }
  tr:nth-child(3) {
    border-top: 1px solid
      ${({ containsAnonymity }: TableProps) =>
        containsAnonymity ? theme.colors.text.disabled : theme.colors.borderSeparator.default};
  }
  @media (max-width: ${theme.breakpoint.small}) {
    th > p {
      word-spacing: 100vw;
    }
  }
`;

type IconContainerProps = {
  bg: string;
  fill?: string;
};
const CheckContainer = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${(p: IconContainerProps) => p.bg};

  & path {
    fill: ${(p: IconContainerProps) => p.fill};
  }

  @media (max-width: ${theme.breakpoint.small}) {
    align-self: flex-end;
    gap: ${theme.spacing.e};
  }
`;

const SearchAndToggleWrapper = styled.div`
  @media (max-width: ${theme.breakpoint.small}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing.f};
  }
`;

const TogglesWrapper = styled.div`
  @media (max-width: ${theme.breakpoint.small}) {
    margin: 0 !important;
    margin-bottom: ${theme.spacing.f} !important;
    width: 100%;
    flex-direction: column;
    align-items: flex-end;
    gap: ${theme.spacing.f};
  }
`;

const ToggleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  @media (max-width: ${theme.breakpoint.small}) {
    display: flex;
    align-items: flex-end;
    gap: ${theme.spacing.f};
    p {
      margin: 0;
    }
  }
`;

const PaginationContainer = styled.div`
  padding: 0 ${theme.spacing.e} ${theme.spacing.e} ${theme.spacing.e};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RespondentsAnswers = ({
  data,
  accessToken,
  search,
  anonymityEnabled,
  publicAccess,
  reportId,
  isHiring,
}: IProps) => {
  const { t } = useTranslation('Report');
  const { url } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobileView();
  const pushReportEvent = usePushReportEvent();
  const [currentPage, setCurrentPage] = usePageParam();
  const respondentTableRef = useRef<HTMLDivElement>(null);
  useScrollToRespondentTable(respondentTableRef);
  const savedValueWithoutAnswer = localStorage.getItem(`${LS_KEY_WITHOUT_ANSWER}:${accessToken}`);
  const savedValueWithWarning = localStorage.getItem(`${LS_KEY_WITH_WARNING}:${accessToken}`);
  const [filterWithoutAnswer, setFilterWithoutAnswer] = useState(
    savedValueWithoutAnswer && !anonymityEnabled ? JSON.parse(savedValueWithoutAnswer) : false,
  );
  const [filterWithWarning, setFilterWithWarning] = useState(
    savedValueWithWarning ? JSON.parse(savedValueWithWarning) : false,
  );
  const [searchQuery, setSearchQuery] = useState('');

  // Check if current page is more than total pages
  useEffect(() => {
    if (data.length === 0 || filteredRows.length === 0) return;
    const totalPages = Math.floor((filteredRows.length - 1) / RESPONDENTS_ON_PAGE) + 1;
    if (data.length > 0 && currentPage > totalPages) {
      // Remove page param from URL if it is more than total pages
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    filterWithWarning,
    filterWithoutAnswer,
    anonymityEnabled,
    searchQuery,
    currentPage,
    history,
    location.pathname,
    location.search,
    t,
  ]);

  const handleChangeSearchQuery = (value: string) => {
    if (currentPage !== 1 && value !== searchQuery) {
      // Remove page param from URL when searching
      setCurrentPage(1);
    }
    setSearchQuery(value);
  };

  const handleToggleWithoutAnswer = (value: boolean) => {
    if (currentPage !== 1) {
      // Remove page param from URL when filtering
      setCurrentPage(1);
    }
    setFilterWithoutAnswer(value);
    localStorage.setItem(`${LS_KEY_WITHOUT_ANSWER}:${accessToken}`, value.toString());
    if (filterWithWarning && value) {
      setFilterWithWarning(false);
      localStorage.setItem(`${LS_KEY_WITH_WARNING}:${accessToken}`, 'false');
    }
  };
  const handleToggleWithWarning = (value: boolean) => {
    if (currentPage !== 1) {
      // Remove page param from URL when filtering
      setCurrentPage(1);
    }
    setFilterWithWarning(value);
    localStorage.setItem(`${LS_KEY_WITH_WARNING}:${accessToken}`, value.toString());
    if (filterWithoutAnswer && value) {
      setFilterWithoutAnswer(false);
      localStorage.setItem(`${LS_KEY_WITHOUT_ANSWER}:${accessToken}`, 'false');
    }
  };

  const baseUrlForAlerts = `${url}${url.endsWith('/') ? '' : '/'}${URLs.REPORT_ALERT_DETAIL}`;

  if (data.length === 0) {
    return <></>;
  }

  const filteredRows = data.filter((row) => {
    if (filterWithWarning && !row.messages.some((msg) => msg.answers.some((ans) => ans.type !== AnswerType.NEUTRAL))) {
      return false;
    }
    if (filterWithoutAnswer && row.messages.length > 0) {
      return false;
    }
    if (anonymityEnabled && row.messages.length === 0) {
      return false;
    }
    if (searchQuery) {
      const searchWords = searchQuery.split(' ');
      return searchWords.every((word) => {
        const sanitizedWord = removeAccents(word).toLowerCase();
        return (
          removeAccents(row.team).toLowerCase().includes(sanitizedWord) ||
          removeAccents(row.name).toLowerCase().includes(sanitizedWord) ||
          removeAccents(row.position ?? '')
            .toLowerCase()
            .includes(sanitizedWord) ||
          removeAccents(row.isHired ? t('hired') : row.isRejected ? t('rejected') : '-')
            .toLowerCase()
            .includes(sanitizedWord) ||
          row.seqDate?.includes(sanitizedWord)
        );
      });
    }
    return true;
  });

  const rows = filteredRows
    .slice((currentPage - 1) * RESPONDENTS_ON_PAGE, (currentPage - 1) * RESPONDENTS_ON_PAGE + RESPONDENTS_ON_PAGE)
    .map((alert) => {
      const noWarning = alert.messages.filter((msg) =>
        msg.answers.some((ans) => ans.type === AnswerType.WARNING),
      ).length;
      const noAlerts = alert.messages.filter((msg) => msg.answers.some((ans) => ans.type === AnswerType.ALERT)).length;
      const seqDate = alert.seqDate && seqDateToString(new Date(alert.seqDate));
      return {
        id: `${alert.respondentId}:${alert.surveyId}`,
        respondentId: alert.respondentId,
        seqDate,
        date: (
          <BodySmall color={theme.colors.text.primary}>
            <Highlighter
              highlightStyle={{
                fontWeight: '900',
                backgroundColor: 'transparent',
                color: theme.colors.text.primary,
                textDecoration: 'underline',
                padding: 0,
              }}
              textToHighlight={seqDate ?? ''}
              searchWords={searchQuery.split(' ')}
              sanitize={removeAccents}
            />
          </BodySmall>
        ),
        name: (
          <BodySmall color={theme.colors.actionPrimary.default} bold>
            <Highlighter
              highlightStyle={{
                fontWeight: '900',
                backgroundColor: 'transparent',
                color: theme.colors.actionPrimary.active,
                textDecoration: 'underline',
                padding: 0,
              }}
              textToHighlight={alert.name}
              searchWords={searchQuery.split(' ')}
              sanitize={removeAccents}
            />
          </BodySmall>
        ),
        team: (
          <BodySmall color={theme.colors.text.secondary}>
            <Highlighter
              highlightStyle={{
                fontWeight: '900',
                backgroundColor: 'transparent',
                color: theme.colors.actionPrimary.active,
                textDecoration: 'underline',
                padding: 0,
              }}
              textToHighlight={alert.team}
              searchWords={searchQuery.split(' ')}
              sanitize={removeAccents}
            />
          </BodySmall>
        ),
        position: (
          <BodySmall color={theme.colors.text.secondary}>
            <Highlighter
              highlightStyle={{
                fontWeight: '900',
                backgroundColor: 'transparent',
                color: theme.colors.actionPrimary.active,
                textDecoration: 'underline',
                padding: 0,
              }}
              textToHighlight={alert.position ?? ''}
              searchWords={searchQuery.split(' ')}
              sanitize={removeAccents}
            />
          </BodySmall>
        ),
        hiringState: (
          <BodySmall color={theme.colors.text.secondary}>
            <Highlighter
              highlightStyle={{
                fontWeight: '900',
                backgroundColor: 'transparent',
                color: theme.colors.actionPrimary.active,
                textDecoration: 'underline',
                padding: 0,
              }}
              textToHighlight={alert.isHired ? t('hired') : alert.isRejected ? t('rejected') : '-'}
              searchWords={searchQuery.split(' ')}
              sanitize={removeAccents}
            />
          </BodySmall>
        ),
        status:
          alert.messages.length > 0 ? (
            <Tooltip title={t('withAnswer')}>
              <CheckContainer bg={theme.colors.emotionSuccess.background} fill={theme.colors.emotionSuccess.default}>
                <DeprecatedCheckIcon />
              </CheckContainer>
            </Tooltip>
          ) : (
            <Tooltip title={t('withoutAnswer')}>
              <CheckContainer bg={theme.colors.backgroundBasic.default}>
                <DeprecatedNoIcon />
              </CheckContainer>
            </Tooltip>
          ),
        answersWithWarnings: (
          <div className="d-flex align-items-center">
            {noWarning > 0 && (
              <>
                <HighlightedAnswersIcon type={AnswerType.WARNING} />{' '}
                <BodySmall color={theme.colors.text.secondary} margin={`0 ${theme.spacing.f} 0 ${theme.spacing.c}`}>
                  ({noWarning})
                </BodySmall>
              </>
            )}
            {noAlerts > 0 && (
              <>
                <HighlightedAnswersIcon type={AnswerType.ALERT} />{' '}
                <BodySmall color={theme.colors.text.secondary} margin={`0 0 0 ${theme.spacing.c}`}>
                  ({noAlerts})
                </BodySmall>
              </>
            )}
          </div>
        ),
        nameTeam: (
          <div className="d-flex flex-column">
            <BodySmall color={theme.colors.actionPrimary.default} bold>
              <Highlighter
                highlightStyle={{
                  fontWeight: '900',
                  backgroundColor: 'transparent',
                  color: theme.colors.actionPrimary.active,
                  textDecoration: 'underline',
                  padding: 0,
                }}
                textToHighlight={alert.name}
                searchWords={searchQuery.split(' ')}
                sanitize={removeAccents}
              />
            </BodySmall>
            <BodySmall color={theme.colors.text.secondary}>{alert.team}</BodySmall>
            <div className="d-flex align-items-center">
              {noWarning > 0 && (
                <>
                  <HighlightedAnswersIcon type={AnswerType.WARNING} />{' '}
                  <BodySmall color={theme.colors.text.secondary} margin={`0 ${theme.spacing.f} 0 ${theme.spacing.c}`}>
                    ({noWarning})
                  </BodySmall>
                </>
              )}
              {noAlerts > 0 && (
                <>
                  <HighlightedAnswersIcon type={AnswerType.ALERT} />{' '}
                  <BodySmall color={theme.colors.text.secondary} margin={`0 0 0 ${theme.spacing.c}`}>
                    ({noAlerts})
                  </BodySmall>
                </>
              )}
            </div>
          </div>
        ),
        namePositionHiringState: (
          <div className="d-flex flex-column">
            <BodySmall color={theme.colors.actionPrimary.default} bold>
              <Highlighter
                highlightStyle={{
                  fontWeight: '900',
                  backgroundColor: 'transparent',
                  color: theme.colors.actionPrimary.active,
                  textDecoration: 'underline',
                  padding: 0,
                }}
                textToHighlight={alert.name}
                searchWords={searchQuery.split(' ')}
                sanitize={removeAccents}
              />
            </BodySmall>
            <BodySmall color={theme.colors.text.secondary}>{alert.position}</BodySmall>
            <BodySmall color={theme.colors.text.secondary}>
              {alert.isHired ? t('hired') : alert.isRejected ? t('rejected') : '-'}
            </BodySmall>
            <div className="d-flex align-items-center">
              {noWarning > 0 && (
                <>
                  <HighlightedAnswersIcon type={AnswerType.WARNING} />{' '}
                  <BodySmall color={theme.colors.text.secondary} margin={`0 ${theme.spacing.f} 0 ${theme.spacing.c}`}>
                    ({noWarning})
                  </BodySmall>
                </>
              )}
              {noAlerts > 0 && (
                <>
                  <HighlightedAnswersIcon type={AnswerType.ALERT} />{' '}
                  <BodySmall color={theme.colors.text.secondary} margin={`0 0 0 ${theme.spacing.c}`}>
                    ({noAlerts})
                  </BodySmall>
                </>
              )}
            </div>
          </div>
        ),
      };
    })
    .sort((a, b) => {
      if (a.seqDate && b.seqDate) {
        return new Date(b.seqDate).getTime() - new Date(a.seqDate).getTime();
      }
      return 0;
    });

  const containsAnonymity = rows[0]?.id === ANONYMOUS_RESPONDENT_ID;
  const onlyAnonymousAnswers = containsAnonymity && rows.length === 1;

  return (
    <div
      ref={respondentTableRef}
      className={'mt-9'}
      style={{ position: 'relative' }}
      data-icom={'report-alerts'}
      data-cy={'report-alert-widget'}
    >
      <div className={`bg-white rounded-lg shadow-sm mb-1 px-8 py-6`}>
        <h3 className={'mb-0'} data-icom={'report-alerts-title'}>
          {t('answersByRespondents')}
        </h3>
        {containsAnonymity && (
          <p className={'mb-0 font-size-xs'}>
            <Trans
              i18nKey="Report:anonymityAnswersInfo"
              components={[
                <strong />,
                <a
                  href="https://intercom.help/arnold-robot/cs/articles/6421241-prace-s-arnoldovymi-reporty#h_4df0a074d7"
                  target="__blank"
                />,
              ]}
            />
          </p>
        )}
      </div>
      <div className={`bg-white rounded-lg shadow-sm mb-1 px-8 py-6`}>
        <SearchAndToggleWrapper className={`d-flex justify-content-between align-items-center mb-6`}>
          <StyledSearchInput
            onChange={handleChangeSearchQuery}
            value={searchQuery}
            delay={500}
            name="respondents-search"
          />
          {rows.length > 0 && (
            <TogglesWrapper className="d-flex ml-10 mt-4">
              {!anonymityEnabled && (
                <ToggleWrapper>
                  <BodyMedium margin={`0 ${theme.spacing.d} 0 0`}>{t('filterWithoutAnswer')}</BodyMedium>
                  <ToggleButton
                    value={filterWithoutAnswer}
                    className="mb-0"
                    onChange={handleToggleWithoutAnswer}
                    dataIcom={'hide-respondents-without-answer-toggle'}
                    dataCy={'hide-respondents-without-answer-toggle'}
                  />
                </ToggleWrapper>
              )}
              {!onlyAnonymousAnswers && (
                <ToggleWrapper>
                  <BodyMedium margin={`0 ${theme.spacing.d} 0 ${theme.spacing.h}`}>
                    {t('filterWithWarnings')}
                  </BodyMedium>
                  <ToggleButton
                    value={filterWithWarning}
                    className="mb-0"
                    onChange={handleToggleWithWarning}
                    dataIcom={'hide-respondents-with-warning-toggle'}
                    dataCy={'hide-respondents-with-warning-toggle'}
                  />
                </ToggleWrapper>
              )}
            </TogglesWrapper>
          )}
        </SearchAndToggleWrapper>
        <StyledTable
          containsAnonymity={containsAnonymity}
          labels={{ emptyTableLabel: t('table:emptyTableLabel') }}
          columns={
            isMobile
              ? [
                  isHiring
                    ? {
                        key: 'namePositionHiringState',
                        title: `${t('respondent')} ${t('position')} ${t('hiringState')}`,
                        dataIndex: 'namePositionHiringState',
                      }
                    : {
                        key: 'nameTeam',
                        title: `${t('respondent')} ${t('team')}`,
                        dataIndex: 'nameTeam',
                      },
                  {
                    key: 'status',
                    title: t('status'),
                    dataIndex: 'status',
                  },
                ]
              : [
                  rows.some((r) => r.seqDate)
                    ? {
                        key: 'date',
                        title: t('date'),
                        dataIndex: 'date',
                      }
                    : null,
                  {
                    key: 'name',
                    title: t('respondent'),
                    dataIndex: 'name',
                  },
                  isHiring
                    ? {
                        key: 'position',
                        title: t('position'),
                        dataIndex: 'position',
                      }
                    : null,
                  isHiring
                    ? {
                        key: 'hiringState',
                        title: t('hiringState'),
                        dataIndex: 'hiringState',
                      }
                    : null,
                  !isHiring
                    ? {
                        key: 'team',
                        title: t('team'),
                        dataIndex: 'team',
                      }
                    : null,
                  {
                    key: 'status',
                    title: t('status'),
                    dataIndex: 'status',
                  },
                  {
                    key: 'answersWithWarnings',
                    title: t('answersWithWarnings'),
                    dataIndex: 'answersWithWarnings',
                  },
                ]
                  .filter((x) => x)
                  .map((x) => x!)
          }
          dataSources={rows}
          onClick={(r) => {
            setScrollToParam(history);
            history.push(
              `${baseUrlForAlerts}/${publicAccess ? getEncodedCryptoAES(r.respondentId, reportId) : r.respondentId}${
                r.seqDate ? `/${r.seqDate}` : ''
              }${search}`,
            );
          }}
        />
        <PaginationContainer>
          <span style={{ marginTop: 20, marginRight: 8 }} data-cy="report-alerts-pagination">
            {t('paginationInfoText', {
              total: filteredRows.length,
              from: (currentPage - 1) * RESPONDENTS_ON_PAGE + 1,
              to: (currentPage - 1) * RESPONDENTS_ON_PAGE + rows.length,
            })}
          </span>
          <Pagination
            totalPages={Math.floor((filteredRows.length - 1) / RESPONDENTS_ON_PAGE) + 1}
            currentPage={currentPage}
            onPageChange={(newPage: number) => {
              pushReportEvent('employee_table_paginated', 'paginate', {
                from_page: currentPage,
                to_page: newPage,
              });
              setCurrentPage(newPage);
            }}
          />
        </PaginationContainer>
      </div>
    </div>
  );
};

export const Answer = styled.div<{ noMaxHeight?: boolean }>`
  background-color: #f9f9f9;
  display: flex;
  flex-direction: row;
  padding: 8px 16px;
  min-height: 38.5px;
  min-width: 361px;
  max-width: 576px;
  overflow-y: auto;
  word-break: break-word;
  width: fit-content;
  max-height: ${({ noMaxHeight }) => (noMaxHeight ? 'none' : '130px')};

  @media (max-width: ${theme.breakpoint.small}) {
    padding-right: ${theme.spacing.e};
    min-width: initial !important;
    width: 100% !important;
    min-width: 10px;
  }
`;

export default RespondentsAnswers;
